import {useRouter} from 'next/router';
import {useEffect, useMemo, useRef} from 'react';
import useGlobals from "@/hooks/useGlobals";
import usePagination from "@/hooks/usePagination";
import useScrollContext from "@/hooks/useScrollContext";
import {useBranch} from "@/hooks/useBranch";

function useFilterNews(data, categories) {
	const router = useRouter();
	const newsItemsPerPage = useGlobals(state => state.newsItemsPerPage)
	const { query } = router;
	const scroll = useScrollContext()
	const ContainerRef = useRef()
	const currentPage = parseInt(query.page, 10) || 1;
	const { selectedOption } = useBranch()
	const ARTICLES_PER_PAGE = newsItemsPerPage || 12;

	const selectedCategoriesFromQuery = useMemo(() => {
		if (!query.categories) return [];
		return query.categories.split('_');
	}, [query.categories]);

	const startDateFromQuery = useMemo(() => query.startDate || null, [query.startDate]);
	const endDateFromQuery = useMemo(() => query.endDate || null, [query.endDate]);

	const dropdownCategories = useMemo(() => {
		return categories.map((item) => {
			const amount = data.filter(article =>
				article.chooseCategories.some(chosenCategory =>
					chosenCategory.id === item.id
				)
			).length;

			return {
				disabled: false,
				id: item.id,
				value: item.urlParameter,
				label: item.name,
				urlParameter: item.urlParameter,
				amount,
			}});
	}, [categories]);

	const filteredData = useMemo(() => {
		let result = data;

		if (selectedCategoriesFromQuery.length > 0) {
			result = data.filter((item) =>
				item.chooseCategories.some((chosenCategory) =>
					selectedCategoriesFromQuery.includes(
						categories.find(cat => cat.id === chosenCategory.id)?.urlParameter
					)
				)
			)
		}

		if (startDateFromQuery || endDateFromQuery) {
			result = result.filter((item) => {
				const createdAt = new Date(item._createdAt);
				const formattedCreatedAt = createdAt.toISOString().slice(0, 10);
				const formattedStartDate = startDateFromQuery ? new Date(startDateFromQuery).toISOString().slice(0, 10) : null;
				const formattedEndDate = endDateFromQuery ? new Date(endDateFromQuery).toISOString().slice(0, 10) : null;

				const afterStartDate = formattedStartDate ? formattedCreatedAt >= formattedStartDate : true;
				const beforeEndDate = formattedEndDate ? formattedCreatedAt <= formattedEndDate : true;

				return afterStartDate && beforeEndDate;
			});
		}

		return result
	}, [data, selectedCategoriesFromQuery, categories, startDateFromQuery, endDateFromQuery]);


	const [currentArticles, totalPages] = usePagination(filteredData, currentPage, ARTICLES_PER_PAGE)

	const handleCategoryChange = (selected) => {
		const selectedIds = selected.target.value.map((category) => category.urlParameter).join('_');

		const updatedQuery = { ...query };

		if (selectedIds) {
			updatedQuery.categories = selectedIds;
		} else {
			delete updatedQuery.categories;
		}

		updatedQuery.page = 1;

		router.replace({
			pathname: router.pathname,
			query: updatedQuery,
		}, undefined, { shallow: true });
	};

	const handleStartDateChange = (event) => {
		const date = event.target.value;
		const updatedQuery = { ...query };

		if (date) {
			updatedQuery.startDate = date;
		} else {
			delete updatedQuery.startDate
		}

		updatedQuery.page = 1;

		router.replace(
			{
				pathname: router.pathname,
				query: updatedQuery,
			},
			undefined,
			{ shallow: true }
		);
	};

	const handleEndDateChange = (event) => {
		const date = event.target.value;
		const updatedQuery = { ...query };

		if (date) {
			updatedQuery.endDate = date;
		} else {
			delete updatedQuery.endDate
		}

		updatedQuery.page = 1;

		router.replace(
			{
				pathname: router.pathname,
				query: updatedQuery,
			},
			undefined,
			{ shallow: true }
		);
	};

	const handlePageClick = (pageNumber) => {
		const updatedQuery = { ...query, page: pageNumber };

		router.replace({
			pathname: router.pathname,
			query: updatedQuery,
		}, undefined, { shallow: true })

		const { scroll: currScroll } = scroll.props()
		const target = ContainerRef.current.getBoundingClientRect().top + currScroll - 80

		if(target > currScroll) return //skip scrolling if had to scroll down

		scroll.scrollTo({
			top: target
		});
	};

	const handleResetFiltration = () => {
		const newQuery = router.query
		newQuery.categories && delete newQuery.categories
		newQuery.startDate && delete newQuery.startDate
		newQuery.endDate && delete newQuery.endDate

		router.replace({
			pathname: router.pathname,
			query: {
				...newQuery,
				page: 1
			}
		}, undefined, { shallow: true })
	}

	const handleRemoveSingleFilter = (filterName, filterValue) => {
		const updatedQuery = { ...query };

		if (filterName === 'categories' && updatedQuery.categories) {
			const categories = updatedQuery.categories.split('_').filter(category => category !== filterValue);
			if (categories.length) {
				updatedQuery.categories = categories.join('_');
			} else {
				delete updatedQuery.categories;
			}
		} else if (updatedQuery[filterName]) {
			delete updatedQuery[filterName];
		}

		updatedQuery.page = 1;

		router.replace({
			pathname: router.pathname,
			query: updatedQuery,
		}, undefined, { shallow: true });
	};

	const isFilterApplied = useMemo(() => {
		return (
			selectedCategoriesFromQuery.length > 0 ||
			!!startDateFromQuery ||
			!!endDateFromQuery
		);
	}, [selectedCategoriesFromQuery, startDateFromQuery, endDateFromQuery]);

	return {
		filteredData: currentArticles,
		totalPages,
		currentPage,
		dropdownCategories,
		selectedCategoriesFromQuery,
		startDateFromQuery,
		endDateFromQuery,
		handleCategoryChange,
		handleStartDateChange,
		handleEndDateChange,
		handlePageClick,
		handleResetFiltration,
		handleRemoveSingleFilter,
		isFilterApplied,
		ContainerRef
	};
}

export {
	useFilterNews
}
